import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useDeleteNumberRequest } from "@/features/number-requests/hooks/use-number-requests";
import {
  isNumberRequestValidated,
  type NumberRequest,
} from "@/features/number-requests/types";
import { useTranslation } from "@/features/utils/i18n";
import { DotsThreeIcon, EyeIcon, TrashIcon } from "@phosphor-icons/react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

/**
 * The menu offers exactly the transitions the backend would accept — the
 * allowed-transition table is mirrored in `features/number-requests/types.ts`.
 * The server still validates; this just avoids offering a button that errors.
 */
export const Action = ({ request }: { request: NumberRequest }) => {
  const { t } = useTranslation("numberRequests");
  const { company: clientId } = useParams<{ company: string }>();

  const remove = useDeleteNumberRequest(clientId || "");

  const handleDelete = async () => {
    try {
      await remove.mutateAsync(request.id);
      toast.success(t("numberRequests.messages.deleted"));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t("numberRequests.messages.deleteFailed"),
      );
    }
  };

  return (
    <div className="flex items-center justify-end gap-1">
      {/* "Voir" is the primary action on a row — everything a client needs to
          do (upload, switch kind, read the procedure) lives on the detail page,
          so it gets a button rather than a menu entry. */}
      <Button variant="outline" size="sm" asChild>
        <Link
          to={`/${clientId}/settings/connectors/number-requests/${request.id}`}
        >
          <EyeIcon className="h-4 w-4" />
          {t("numberRequests.actions.view")}
        </Link>
      </Button>

      {/* A validated request is the file behind a live line — deleting it would
          take the papers we filed with it and leave the number unexplained. The
          backend refuses it too; this just doesn't offer it. */}
      {!isNumberRequestValidated(request) && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <DotsThreeIcon className="h-4 w-4" weight="bold" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleDelete} className="text-red-500">
              <TrashIcon className="h-4 w-4" />
              {t("numberRequests.actions.delete")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
};
